import React, { useState } from 'react';

import { Icon, IconName } from '@betterment/samba-react';
import { Box, Flex, theme } from '@betterment/style-closet-react';
import ContentLoader from 'react-content-loader';

import { ThemedImage } from '../../Shared/components-v2/themed-image/ThemedImage';
import { RestThemedAsset } from '../../Shared/data';

export type TransactionCategory = keyof typeof CategoryIconMap;

export const CategoryIconMap = Object.freeze({
  'ATM Fee Reimbursement': 'Dollar',
  'ATM Withdrawal': 'AtmCard',
  'Auto & Transport': 'Car',
  'Bills & Utilities': 'Bills',
  'Business Services': 'Briefcase',
  'Cash Back Reward': 'Dollar',
  'Check Deposit': 'Document',
  'Check Deposit Return': 'Document',
  'Check Withdrawal': 'Document',
  Credit: 'Dollar',
  'Credit Card Payment': 'Withdraw',
  'Debit Card Purchase': 'Card',
  Education: 'Education',
  Entertainment: 'Ticket',
  'External Deposit': 'Dollar',
  'External Withdrawal': 'Withdraw',
  'Fees & Charges': 'MajorPurchase',
  Financial: 'Bank',
  'Food & Dining': 'Dining',
  'Gifts & Donations': 'Present',
  'Health & Fitness': 'Fitness',
  Home: 'Home',
  Income: 'Cash',
  'Internal Transfer': 'Transfer',
  'International Fee': 'Globe',
  'International Fee Reimbursement': 'Dollar',
  Investments: 'Trend',
  Kids: 'Stroller',
  'Merchandise Return': 'Dollar',
  Paycheck: 'Cash',
  'Personal Care': 'Glitter',
  Pets: 'Pets',
  'Returned Deposit': 'ExclamationCircle',
  'Returned Withdrawal': 'ExclamationCircle',
  Reversal: 'ExclamationCircle',
  Shopping: 'Shopping',
  Taxes: 'Calculator',
  Travel: 'Map',
  'Verification Deposit': 'Dollar',
  'Verification Withdrawal': 'Withdraw',
  Uncategorized: 'Shapes',
} as const satisfies Record<string, IconName>);

const IconShimmer: React.FC<React.PropsWithChildren<unknown>> = () => (
  <ContentLoader
    style={{ display: 'block' }}
    gradientRatio={2}
    height={36}
    width={36}
    speed={0.99}
    interval={0.2}
    primaryColor={theme.colors.bmtGrey10}
    secondaryColor={theme.colors.bmtGrey20}
  >
    <rect x="0" y="0" rx="5" ry="5" width="36" height="36" />
  </ContentLoader>
);

const TransactionIcon: React.FC<
  React.PropsWithChildren<{
    icon?: RestThemedAsset;
    category?: TransactionCategory;
    size?: 'small' | 'large';
  }>
> = ({ icon, category = 'Uncategorized', size = 'large' }) => {
  const [imageLoadingState, setImageLoadingState] = useState('loading');
  const height = size === 'small' ? 36 : 72;

  if (icon && imageLoadingState !== 'error') {
    const asset = { light: icon.lightUrl, dark: icon.darkUrl };

    return (
      <>
        {imageLoadingState === 'loading' && (
          <Box width={height} height={height} minWidth={height} maxWidth={height}>
            <IconShimmer />
          </Box>
        )}
        <ThemedImage
          themedAsset={asset}
          style={{ borderRadius: 8 }}
          height={height}
          width={height}
          alt={category}
          onLoad={() => setImageLoadingState('loaded')}
          onError={() => setImageLoadingState('error')}
        />
      </>
    );
  }

  const iconName = CategoryIconMap[category] ?? CategoryIconMap.Uncategorized;

  return (
    <Flex
      height={height}
      width={height}
      backgroundColor="bmtGrey10"
      borderRadius={8}
      justifyContent="space-around"
      alignItems="center"
    >
      <Icon name={iconName} size={size} color="icon" mode="functional" label={category} />
    </Flex>
  );
};

export default TransactionIcon;
